import { useState, useEffect } from 'react';
import { Toaster } from 'sonner';
import BookingFlow from './components/BookingFlow';
import AdminDashboard from './components/AdminDashboard';
import Auth from './components/Auth';
import { PricingPage } from './components/PricingPage';
import { SuperAdminDashboard } from './components/SuperAdminDashboard';
import { isSupabaseConfigured, supabase } from './lib/supabase';
import { Sun, Moon, Scissors, LayoutDashboard, User, AlertCircle, Plus, History, LogOut, ShieldCheck, CreditCard } from 'lucide-react';
import { useLanguage } from './lib/LanguageContext';
import { LanguageSwitcher } from './components/LanguageSwitcher';
import { AuthProvider, useAuth } from './lib/AuthContext';
import { ThemeProvider, useTheme } from './lib/ThemeContext';
import { CookieConsent, Footer } from './components/Legal';
import { Logo } from './components/Logo';

function AppContent() {
  const { t, language } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const { user, profile, subscription, loading: authLoading, signOut, refreshProfile } = useAuth();
  
  const getInitialView = () => {
    const params = new URLSearchParams(window.location.search);
    const viewParam = params.get('view');
    if (viewParam === 'admin' || viewParam === 'super-admin' || viewParam === 'pricing' || viewParam === 'customer') {
      return viewParam as 'customer' | 'admin' | 'super-admin' | 'pricing';
    }
    return 'customer';
  };

  const [view, setView] = useState<'customer' | 'admin' | 'super-admin' | 'pricing'>(getInitialView());
  const [mode, setMode] = useState<'booking' | 'history'>('booking');
  const [dbError, setDbError] = useState<string | null>(null);

  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  useEffect(() => {
    if (!isSupabaseConfigured) return;

    const checkDb = async () => {
      try {
        const { error } = await supabase.from('services').select('id').limit(1);
        if (error) {
          if (error.code === 'PGRST204' || error.message.includes('relation "services" does not exist')) {
            setDbError(t('setup.dbSchemaNotFound'));
          } else {
            setDbError(error.message);
          }
        }
      } catch (err: any) {
        setDbError(err.message);
      }
    };

    checkDb();

    // Check for Stripe session success
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('session_id')) {
      setView('admin');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [t]);

  if (!isSupabaseConfigured || dbError) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white p-10 rounded-[3rem] shadow-2xl border border-zinc-100 text-center">
          <div className="inline-block p-4 bg-amber-50 text-amber-600 rounded-2xl mb-6">
            <AlertCircle size={32} />
          </div>
          <h1 className="text-3xl font-black uppercase tracking-tighter mb-4">
            {!isSupabaseConfigured ? t('setup.required') : t('setup.dbError')}
          </h1>
          <p className="text-zinc-500 font-medium mb-8 leading-relaxed">
            {!isSupabaseConfigured 
              ? t('setup.configureSupabase')
              : dbError}
          </p>
          {!isSupabaseConfigured ? (
            <div className="space-y-2 text-left bg-zinc-50 p-6 rounded-2xl border border-zinc-100">
              <p className={`font-black text-zinc-400 mb-2 ${language === 'ta' ? 'text-xs' : 'text-[10px] uppercase tracking-widest'}`}>{t('setup.requiredVariables')}</p>
              <code className="block text-xs font-bold text-zinc-600">VITE_SUPABASE_URL</code>
              <code className="block text-xs font-bold text-zinc-600">VITE_SUPABASE_ANON_KEY</code>
            </div>
          ) : (
            <button 
              onClick={() => window.location.reload()}
              className="w-full py-4 bg-zinc-900 text-white rounded-2xl font-black uppercase tracking-widest hover:bg-zinc-800 transition-all"
            >
              {t('common.retry')}
            </button>
          )}
        </div>
      </div>
    );
  }

  if (authLoading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-zinc-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const isTestAccount = user?.email?.toLowerCase().includes('test') || 
                        user?.email?.toLowerCase().includes('admin') || 
                        user?.email?.toLowerCase() === 'devthines@gmail.com';
                        
  const isSuperAdmin = profile?.role === 'super_admin';
  const isAdmin = profile?.role === 'barber_admin' || profile?.role === 'barber_staff' || isSuperAdmin || isTestAccount;
  
  // Bypass subscription lock for testing
  const hasActiveSubscription = subscription?.status === 'active' || subscription?.status === 'trialing' || isSuperAdmin || isTestAccount;
  const isPricing = view === 'pricing';
  
  const handleLogoClick = () => {
    setView('customer');
    setMode('booking');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className={`min-h-screen transition-colors duration-500 ${theme} ${isPricing ? 'bg-black' : (theme === 'dark' ? 'bg-zinc-950 text-white' : 'bg-zinc-50')}`}>
      <nav className="fixed top-0 md:top-6 left-0 right-0 z-50 px-0 md:px-6 pointer-events-none">
        <div className={`max-w-5xl mx-auto backdrop-blur-xl border-b md:border shadow-2xl px-4 md:px-8 py-3 md:py-4 flex items-center justify-between pointer-events-auto transition-all duration-500 ${
          isPricing 
            ? 'bg-black/80 md:bg-black/90 border-zinc-800 md:rounded-[2.5rem] shadow-black/20' 
            : (theme === 'dark' 
                ? 'bg-zinc-900/80 md:bg-zinc-900/90 border-zinc-800 md:rounded-[2.5rem] shadow-black/20' 
                : 'bg-white/80 md:bg-white/90 border-zinc-200/50 md:rounded-[2.5rem] shadow-zinc-200/20')
        }`}>
          <Logo 
            variant={isPricing || theme === 'dark' ? 'light' : 'dark'}
            onClick={handleLogoClick}
          />

          <div className="flex items-center gap-2 md:gap-4">
            <div className={`flex items-center gap-1 md:gap-2 p-1 md:p-1.5 rounded-xl md:rounded-2xl border transition-colors duration-500 ${
              isPricing || theme === 'dark' ? 'bg-zinc-900/50 border-zinc-800' : 'bg-zinc-100/50 border-zinc-200/50'
            }`}>
              <button 
                onClick={() => {
                  setView('customer');
                  setMode('booking');
                }}
                aria-label={t('nav.book')}
                title="Explore and book your professional barber service appointment"
                aria-current={view === 'customer' && mode === 'booking' ? 'page' : undefined}
                className={`flex items-center gap-2 px-3 md:px-5 py-2 md:py-2.5 rounded-lg md:rounded-xl text-[10px] md:text-xs font-black uppercase tracking-widest transition-all duration-500 ${
                  view === 'customer' && mode === 'booking'
                    ? (isPricing || theme === 'dark' ? 'bg-white text-zinc-900 shadow-xl' : 'bg-white text-zinc-900 shadow-xl shadow-zinc-200/50 scale-[1.02]') 
                    : (isPricing || theme === 'dark' ? 'text-zinc-500 dark:text-zinc-300 hover:text-white' : 'text-zinc-500 dark:text-zinc-300 hover:text-zinc-900 dark:hover:text-white hover:bg-zinc-200/30')
                } shadow-sm`}
              >
                <Scissors size={14} className="md:w-4 md:h-4" aria-hidden="true" />
                <span className="hidden xs:inline">{t('nav.book')}</span>
              </button>
              
              {isAdmin && (
                <button 
                  onClick={() => setView('admin')}
                  aria-label={t('nav.admin')}
                  title={t('nav.admin')}
                  aria-current={view === 'admin' ? 'page' : undefined}
                  className={`flex items-center gap-2 px-3 md:px-5 py-2 md:py-2.5 rounded-lg md:rounded-xl text-[10px] md:text-xs font-black uppercase tracking-widest transition-all duration-500 ${
                    view === 'admin' 
                      ? (isPricing || theme === 'dark' ? 'bg-white text-zinc-900 shadow-xl' : 'bg-white text-zinc-900 shadow-xl shadow-zinc-200/50 scale-[1.02]') 
                      : (isPricing || theme === 'dark' ? 'text-zinc-500 dark:text-zinc-300 hover:text-white' : 'text-zinc-500 dark:text-zinc-300 hover:text-zinc-900 dark:hover:text-white hover:bg-zinc-200/30')
                  }`}
                >
                  <LayoutDashboard size={14} className="md:w-4 md:h-4" aria-hidden="true" />
                  <span className="hidden xs:inline">{t('nav.admin')}</span>
                </button>
              )}

              {user && !isAdmin && !isSuperAdmin && (
                <button 
                  onClick={() => {
                    setView('customer');
                    setMode('history');
                  }}
                  aria-label={t('nav.history')}
                  title={t('nav.history')}
                  aria-current={view === 'customer' && mode === 'history' ? 'page' : undefined}
                  className={`flex items-center gap-2 px-3 md:px-5 py-2 md:py-2.5 rounded-lg md:rounded-xl text-[10px] md:text-xs font-black uppercase tracking-widest transition-all duration-500 ${
                    view === 'customer' && mode === 'history'
                      ? (isPricing || theme === 'dark' ? 'bg-white text-zinc-900 shadow-xl' : 'bg-white text-zinc-900 shadow-xl shadow-zinc-200/50 scale-[1.02]') 
                      : (isPricing || theme === 'dark' ? 'text-zinc-500 dark:text-zinc-300 hover:text-white' : 'text-zinc-500 dark:text-zinc-300 hover:text-zinc-900 hover:bg-zinc-200/30')
                  }`}
                >
                  <User size={14} className="md:w-4 md:h-4" aria-hidden="true" />
                  <span className="hidden xs:inline">{t('nav.history')}</span>
                </button>
              )}

              {isSuperAdmin && (
                <button 
                  onClick={() => setView('super-admin')}
                  aria-label={t('nav.systemAdmin')}
                  title={t('nav.systemAdmin')}
                  aria-current={view === 'super-admin' ? 'page' : undefined}
                  className={`flex items-center gap-2 px-3 md:px-5 py-2 md:py-2.5 rounded-lg md:rounded-xl text-[10px] md:text-xs font-black uppercase tracking-widest transition-all duration-500 ${
                    view === 'super-admin' 
                      ? (isPricing || theme === 'dark' ? 'bg-white text-zinc-900 shadow-xl' : 'bg-white text-zinc-900 shadow-xl shadow-zinc-200/50 scale-[1.02]') 
                      : (isPricing || theme === 'dark' ? 'text-zinc-500 dark:text-zinc-300 hover:text-white' : 'text-zinc-500 dark:hover:text-zinc-900 hover:bg-zinc-200/30')
                  }`}
                >
                  <ShieldCheck size={14} className="md:w-4 md:h-4" aria-hidden="true" />
                  <span className="hidden xs:inline">{t('nav.systemAdmin')}</span>
                </button>
              )}

              {!isAdmin && !isSuperAdmin && (
                <button 
                  onClick={() => setView('pricing')}
                  aria-label={t('nav.becomePartner')}
                  title={t('nav.becomePartner')}
                  aria-current={view === 'pricing' ? 'page' : undefined}
                  className={`flex items-center gap-2 px-3 md:px-5 py-2 md:py-2.5 rounded-lg md:rounded-xl text-[10px] md:text-xs font-black uppercase tracking-widest transition-all duration-500 ${view === 'pricing' ? 'bg-amber-500 text-white shadow-xl shadow-amber-200/50 scale-[1.02]' : (theme === 'dark' ? 'text-zinc-500 dark:text-zinc-300 hover:text-white' : 'text-zinc-500 hover:text-zinc-600')}`}
                >
                  <CreditCard size={14} className="md:w-4 md:h-4" aria-hidden="true" />
                  <span className="hidden xs:inline">{t('nav.becomePartner')}</span>
                </button>
              )}
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={toggleTheme}
                aria-label={theme === 'dark' ? t('nav.switchLight') : t('nav.switchDark')}
                title={theme === 'dark' ? t('nav.switchLight') : t('nav.switchDark')}
                className={`p-2 md:p-3 rounded-xl md:rounded-2xl transition-all shadow-xl active:scale-95 ${
                  isPricing 
                    ? 'bg-white text-zinc-900 hover:bg-zinc-100 shadow-white/10' 
                    : (theme === 'dark' 
                        ? 'bg-zinc-800 text-amber-400 hover:bg-zinc-700 shadow-black/20' 
                        : 'bg-white text-zinc-600 hover:bg-zinc-50 shadow-zinc-200')
                }`}
              >
                {theme === 'dark' ? <Sun size={18} aria-hidden="true" /> : <Moon size={18} aria-hidden="true" />}
              </button>
              <LanguageSwitcher variant={isPricing || theme === 'dark' ? 'light' : 'dark'} />
              {user && (
                <button 
                  onClick={signOut}
                  aria-label={t('common.logout')}
                  className={`p-2 md:p-3 rounded-xl md:rounded-2xl transition-all shadow-xl active:scale-95 ${
                    isPricing 
                      ? 'bg-white text-zinc-900 hover:bg-zinc-100 shadow-white/10' 
                      : 'bg-zinc-900 text-white hover:bg-zinc-800 shadow-zinc-200'
                  }`}
                  title={t('common.logout')}
                >
                  <LogOut size={18} className="md:w-5 md:h-5" aria-hidden="true" />
                </button>
              )}
            </div>
          </div>
        </div>
      </nav>

      <main className="pt-20 md:pt-36 pb-12 px-4 md:px-6">
        {view === 'pricing' ? (
          <PricingPage />
        ) : view === 'super-admin' ? (
          <SuperAdminDashboard />
        ) : view === 'admin' ? (
          user ? (
            isAdmin && hasActiveSubscription ? (
              <AdminDashboard profile={profile} subscription={subscription} refreshProfile={refreshProfile} />
            ) : (
              <div className="max-w-md mx-auto mt-20 text-center">
                <div className="bg-white p-12 rounded-[3rem] border border-zinc-100 shadow-2xl">
                  <div className="w-20 h-20 bg-amber-50 text-amber-500 rounded-3xl flex items-center justify-center mx-auto mb-8">
                    <CreditCard size={40} />
                  </div>
                  <h2 className="text-2xl font-black uppercase tracking-tighter mb-4">{t('pricing.subRequired')}</h2>
                  <p className="text-zinc-500 font-medium mb-8">
                    {t('pricing.subRequiredDesc')}
                  </p>
                  <button 
                    onClick={() => setView('pricing')}
                    className="w-full py-4 bg-zinc-900 text-white rounded-2xl font-black uppercase tracking-widest hover:bg-zinc-800 transition-all"
                  >
                    {t('pricing.viewPricing')}
                  </button>
                </div>
              </div>
            )
          ) : (
            <Auth onAuthSuccess={() => setView('admin')} onLogoClick={handleLogoClick} />
          )
        ) : (
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center justify-center gap-4 mb-12">
              <button 
                onClick={() => setMode('booking')}
                className={`flex items-center gap-2 px-8 py-4 rounded-2xl text-xs font-black uppercase tracking-widest transition-all ${mode === 'booking' ? 'bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 shadow-2xl shadow-zinc-200 dark:shadow-black/50 scale-105' : 'bg-white dark:bg-zinc-900 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200'}`}
              >
                <Plus size={18} /> {t('nav.book')}
              </button>
              <button 
                onClick={() => setMode('history')}
                className={`flex items-center gap-2 px-8 py-4 rounded-2xl text-xs font-black uppercase tracking-widest transition-all ${mode === 'history' ? 'bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 shadow-2xl shadow-zinc-200 dark:shadow-black/50 scale-105' : 'bg-white dark:bg-zinc-900 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200'}`}
              >
                <History size={18} /> {t('nav.history')}
              </button>
            </div>
            
            {mode === 'history' && !user ? (
              <Auth onAuthSuccess={() => setMode('history')} onLogoClick={handleLogoClick} />
            ) : (
              <BookingFlow user={user} profile={profile} mode={mode} setMode={setMode} />
            )}
          </div>
        )}
      </main>
      <Footer onLogoClick={handleLogoClick} onViewPricing={() => setView('pricing')} />
      <Toaster position="top-center" richColors />
      <CookieConsent />
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
